import { wouldCreateCycle } from '../engine/dag';
import { emptyInputs } from '../engine/evaluate';
import {
    ignoreGroupForLine,
    listEligibleIgnoreGroups,
} from '../engine/ignoreGroups';
import { allowedDrivers } from '../engine/schema';
import { migrateTotalOrdersDriversToAvgOrders } from '../migrateTotalOrdersDrivers';
import { materialize } from '../engine/scurve';
import type {
    CaseInputs,
    CashPolicy,
    CashPolicyYieldView,
    DriverType,
    History,
    LineDriverState,
    OneTimeItem,
    Recipe,
    SchemaEntry,
    View,
} from '../engine/types';
import { driverInputUnit } from '../driverUtils';
import { materialise, validateRecipe } from './recipe';
import {
    anchorFromHistory,
    checkAnchorDrift,
    resolveSeasonalityFactors,
} from './recipeHistory';
import { LOCATION_VIEWS } from '../engine/types';
import { rebaseMaterialized } from './rebase';
import type {
    CaseFileCashPolicy,
    CaseFileLine,
    CaseFileOneTimeItem,
    CaseFileSummary,
    CaseFileV1,
    ExistingCaseInfo,
    FileIssue,
    ParsedCaseFile,
} from './types';
import { FILE_ISSUE_CODES, FILE_VIEWS, PERCENT_DRIVERS } from './types';

export const RETIRED_LINE_KEYS = new Set([
    'bs.tax_distributions_partner_1',
    'bs.tax_distributions_partner_2',
    'bs.tax_distributions_partner_3',
    'bs.tax_distributions_partner_4',
    'bs.tax_distributions_partner_5',
    'bs.tax_distributions_partner_6',
    'bs.tax_distributions_partner_7',
    'bs.less_tax_distributions',
    'cf.less_tax_distributions',
]);
import {
    blankCashPolicy,
    collectTemplateLineKeys,
    fileMonthFromLastClosedDate,
    hasNonCarryForwardDriver,
    isBlankCashPolicyFile,
    lastClosedDateFromFileMonth,
    lineDisplayName,
    monthIndex,
    projectionMonthKeys,
    trimSchemaLabel,
} from './utils';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const TOP_LEVEL_KEYS = new Set([
    'format',
    'format_version',
    '_guide',
    '_reference',
    'case',
    'basis',
    'consolidated_only',
    'lines',
    'one_time_items',
    'cash_policy',
    'notes',
]);
const LINE_REF_KEYS = new Set([
    'label',
    'unit',
    'allowed_drivers',
    'history',
    'history_summary',
    'ignore_group_members',
]);
const LINE_DATA_KEYS = new Set([
    ...LINE_REF_KEYS,
    'driver',
    'values',
    'params',
]);
const CASE_KEYS = new Set(['name', 'description', 'expires_on']);
const BASIS_KEYS = new Set(['last_closed_month', 'months', 'schema_fingerprint']);
const CASH_POLICY_KEYS = new Set(['enabled', 'floor', 'distribution', 'sweep', 'solver']);
const CASH_POLICY_FLOOR_KEYS = new Set(['operating_minimum', 'drawdown_allowance']);
const CASH_POLICY_DISTRIBUTION_KEYS = new Set(['cadence', 'anchor_date', 'amount_per_period', 'one_time']);
const CASH_POLICY_SWEEP_KEYS = new Set([
    'enabled',
    'mode',
    'amount_per_period',
    'yield_income_view',
    'opening_mm_balance',
]);
const CASH_POLICY_SOLVER_KEYS = new Set(['horizon_months', 'round_to']);
const CASH_POLICY_SERVER_FIELDS = new Set(['split_schedule', 'yield_snapshot']);
const NOTE_CELL_RE =
    /^(river_oaks|highland_park|ga|consolidated):[a-z0-9_.]+:(?:-[1-9][0-9]?|[1-9]|[12][0-9]|3[0-6]|label|methodology)$/;

export interface ParseCaseFileOptions {
    schema: SchemaEntry[];
    lastClosedMonth: string;
    schemaFingerprint: string;
    existingCases?: ExistingCaseInfo[];
    existingNames?: string[];
    today: string;
    rebase?: boolean;
    history?: History;
    recipeDefaults?: { anchor_drift_pct?: number; rationale_max_words?: number };
}

function pushIssue(issues: FileIssue[], issue: FileIssue): void {
    issues.push(issue);
}

function isRecipeObject(raw: unknown): boolean {
    return isObject(raw) && !Array.isArray(raw) && 'anchor' in raw;
}

function isStepMapValues(raw: unknown): boolean {
    if (!isObject(raw) || Array.isArray(raw)) {
        return false;
    }
    if (isRecipeObject(raw)) {
        return false;
    }
    const keys = Object.keys(raw);
    return keys.length > 0 && keys.every((key) => /^\d{4}-\d{2}$/.test(key));
}

function isNonFlatRawValues(raw: unknown): boolean {
    if (raw == null || typeof raw === 'number') {
        return false;
    }
    if (isStepMapValues(raw)) {
        return true;
    }
    if (!Array.isArray(raw)) {
        return false;
    }
    const nonNull = raw.filter((v) => v != null && typeof v === 'number' && Number.isFinite(v));
    if (nonNull.length === 0) {
        return false;
    }
    const distinct = new Set(nonNull);
    if (distinct.size >= 2) {
        return true;
    }
    return false;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function checkUnknownKeys(
    obj: Record<string, unknown>,
    allowed: Set<string>,
    path: string,
    issues: FileIssue[],
): void {
    for (const key of Object.keys(obj)) {
        if (key.startsWith('_')) {
            continue;
        }
        if (!allowed.has(key)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_unknown_key,
                severity: 'error',
                path: `${path}.${key}`,
                message: `Unknown field "${key}" at ${path}.`,
            });
        }
    }
}

function flagCashPolicyServerField(path: string, key: string, issues: FileIssue[]): void {
    pushIssue(issues, {
        code: FILE_ISSUE_CODES.file_cash_policy_server_field,
        severity: 'error',
        path: `${path}.${key}`,
        message: `${key} is server-computed and must not appear in the file.`,
    });
}

function walkCashPolicyKeys(value: unknown, path: string, issues: FileIssue[]): void {
    if (!isObject(value)) {
        return;
    }
    for (const key of Object.keys(value)) {
        if (key.startsWith('_')) {
            continue;
        }
        if (CASH_POLICY_SERVER_FIELDS.has(key)) {
            flagCashPolicyServerField(path, key, issues);
        }
    }
    if (path.endsWith('.cash_policy')) {
        checkUnknownKeys(value, CASH_POLICY_KEYS, path, issues);
    } else if (path.endsWith('.floor')) {
        checkUnknownKeys(value, CASH_POLICY_FLOOR_KEYS, path, issues);
    } else if (path.endsWith('.distribution')) {
        checkUnknownKeys(value, CASH_POLICY_DISTRIBUTION_KEYS, path, issues);
    } else if (path.endsWith('.sweep')) {
        checkUnknownKeys(value, CASH_POLICY_SWEEP_KEYS, path, issues);
    } else if (path.endsWith('.solver')) {
        checkUnknownKeys(value, CASH_POLICY_SOLVER_KEYS, path, issues);
    }
    for (const [key, child] of Object.entries(value)) {
        if (key.startsWith('_') || CASH_POLICY_SERVER_FIELDS.has(key)) {
            continue;
        }
        if (key === 'floor' || key === 'distribution' || key === 'sweep' || key === 'solver') {
            walkCashPolicyKeys(child, `${path}.${key}`, issues);
        }
    }
}

function walkUnknownKeys(value: unknown, path: string, issues: FileIssue[], lineContext = false): void {
    if (!isObject(value)) {
        return;
    }
    if (lineContext) {
        checkUnknownKeys(value, LINE_DATA_KEYS, path, issues);
    } else if (path === '$') {
        checkUnknownKeys(value, TOP_LEVEL_KEYS, path, issues);
    } else if (path.endsWith('.case')) {
        checkUnknownKeys(value, CASE_KEYS, path, issues);
    } else if (path.endsWith('.basis')) {
        checkUnknownKeys(value, BASIS_KEYS, path, issues);
    } else if (path.endsWith('.cash_policy') || path.includes('.cash_policy.')) {
        walkCashPolicyKeys(value, path, issues);
        return;
    }
    for (const [key, child] of Object.entries(value)) {
        if (key.startsWith('_')) {
            continue;
        }
        if (key === 'cash_policy') {
            walkCashPolicyKeys(child, `${path}.${key}`, issues);
            continue;
        }
        if (
            lineContext &&
            (key === 'values' || key === 'params' || key === 'history' || key === 'history_summary')
        ) {
            continue;
        }
        if (path === '$._reference' || path.startsWith('$._reference.')) {
            continue;
        }
        walkUnknownKeys(child, `${path}.${key}`, issues, lineContext);
    }
}

function buildInputsStubFromParsedLines(
    parsedLines: CaseFileV1['lines'],
    consolidatedOnly: string[],
): CaseInputs {
    const inputs = emptyInputs();
    inputs.consolidated_only = [...consolidatedOnly];
    for (const view of FILE_VIEWS) {
        for (const [lineKey, line] of Object.entries(parsedLines[view])) {
            if (line.driver === 'ignore') {
                if (!inputs.drivers[view]) {
                    inputs.drivers[view] = {};
                }
                inputs.drivers[view]![lineKey] = { active: 'ignore', by_type: {} };
            }
        }
    }
    return inputs;
}

function resolveExistingCases(options: ParseCaseFileOptions): ExistingCaseInfo[] {
    if (options.existingCases) {
        return options.existingCases;
    }
    if (options.existingNames) {
        return options.existingNames.map((name) => ({
            name,
            expires_on: null,
            expired: false,
        }));
    }
    return [];
}

function materializeValues(
    raw: unknown,
    months: string[],
    path: string,
    issues: FileIssue[],
): (number | null)[] | null {
    if (Array.isArray(raw)) {
        if (raw.length !== 36) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path,
                message: `Values array on ${path} must have exactly 36 entries.`,
            });
            return null;
        }
        const out: (number | null)[] = [];
        for (let i = 0; i < 36; i += 1) {
            const v = raw[i];
            if (v === null) {
                out.push(null);
            } else if (isFiniteNumber(v)) {
                out.push(v);
            } else {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${path}[${i}]`,
                    message: `Value at ${path}[${i}] must be a finite number or null.`,
                });
                out.push(null);
            }
        }
        return out;
    }
    if (isFiniteNumber(raw)) {
        return Array(36).fill(raw);
    }
    if (isObject(raw)) {
        const keys = Object.keys(raw).sort();
        const stepValues: Record<string, number | null> = {};
        for (const key of keys) {
            if (!months.includes(key)) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${path}.${key}`,
                    message: `Step-map key "${key}" is not a projection month.`,
                });
                continue;
            }
            const v = raw[key];
            if (v === null) {
                stepValues[key] = null;
            } else if (isFiniteNumber(v)) {
                stepValues[key] = v;
            } else {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${path}.${key}`,
                    message: `Step-map value at ${path}.${key} must be a finite number or null.`,
                });
            }
        }
        const out: (number | null)[] = [];
        let last: number | null = null;
        for (const month of months) {
            if (Object.prototype.hasOwnProperty.call(stepValues, month)) {
                last = stepValues[month] ?? null;
            }
            out.push(last);
        }
        return out;
    }
    if (typeof raw === 'string') {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path,
            message: `Values on ${path} cannot be a string; use a JSON number.`,
        });
        return null;
    }
    pushIssue(issues, {
        code: FILE_ISSUE_CODES.file_values_shape,
        severity: 'error',
        path,
        message: `Values on ${path} must be a 36-array, a number, or a month-keyed object.`,
    });
    return null;
}

function parseCashPolicy(
    raw: unknown,
    path: string,
    issues: FileIssue[],
): CashPolicy | null {
    if (raw === null) {
        return null;
    }
    if (!isObject(raw)) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_cash_policy_invalid,
            severity: 'error',
            path,
            message: 'Cash policy must be an object.',
        });
        return null;
    }
    for (const key of Object.keys(raw)) {
        if (CASH_POLICY_SERVER_FIELDS.has(key)) {
            flagCashPolicyServerField(path, key, issues);
        }
    }
    if (isObject(raw.distribution) && 'split_schedule' in raw.distribution) {
        flagCashPolicyServerField(`${path}.distribution`, 'split_schedule', issues);
    }
    if (isObject(raw.sweep) && 'yield_snapshot' in raw.sweep) {
        flagCashPolicyServerField(`${path}.sweep`, 'yield_snapshot', issues);
    }
    const filePolicy = raw as unknown as CaseFileCashPolicy;
    if (isBlankCashPolicyFile(filePolicy)) {
        return null;
    }
    const cadence = filePolicy.distribution?.cadence;
    const anchor = filePolicy.distribution?.anchor_date;
    if (cadence !== 'biweekly' && cadence !== 'monthly') {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_cash_policy_invalid,
            severity: 'error',
            path: `${path}.distribution.cadence`,
            message: 'Cash policy cadence must be biweekly or monthly when enabled.',
        });
    }
    if (!anchor || !/^\d{4}-\d{2}-\d{2}$/.test(anchor)) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_cash_policy_invalid,
            severity: 'error',
            path: `${path}.distribution.anchor_date`,
            message: 'Cash policy anchor_date must be YYYY-MM-DD when enabled.',
        });
    }
    const horizon = filePolicy.solver?.horizon_months;
    if (horizon !== 12 && horizon !== 24 && horizon !== 36) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_cash_policy_invalid,
            severity: 'error',
            path: `${path}.solver.horizon_months`,
            message: 'solver.horizon_months must be 12, 24, or 36.',
        });
    }
    const roundTo = filePolicy.solver?.round_to;
    if (roundTo !== 500 && roundTo !== 1000 && roundTo !== 5000) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_cash_policy_invalid,
            severity: 'error',
            path: `${path}.solver.round_to`,
            message: 'solver.round_to must be 500, 1000, or 5000.',
        });
    }
    if (filePolicy.sweep?.enabled) {
        const view = filePolicy.sweep.yield_income_view;
        if (!view || !LOCATION_VIEWS.includes(view as View)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_cash_policy_invalid,
                severity: 'error',
                path: `${path}.sweep.yield_income_view`,
                message: 'sweep.yield_income_view must be a location view when sweep is enabled.',
            });
        }
    }
    if (issues.some((i) => i.severity === 'error' && i.path.startsWith(path))) {
        return null;
    }
    return {
        enabled: Boolean(filePolicy.enabled),
        floor: {
            operating_minimum: filePolicy.floor?.operating_minimum ?? null,
            drawdown_allowance: filePolicy.floor?.drawdown_allowance ?? null,
        },
        distribution: {
            cadence: cadence as 'biweekly' | 'monthly',
            anchor_date: anchor as string,
            amount_per_period: filePolicy.distribution?.amount_per_period ?? null,
            one_time: (() => {
                const raw = filePolicy.distribution?.one_time;
                if (!raw || typeof raw !== 'object') {
                    return null;
                }
                const otDate = typeof raw.date === 'string' ? raw.date : '';
                const otAmount = typeof raw.amount === 'number' ? raw.amount : null;
                if (!otDate || otAmount == null || otAmount === 0) {
                    return null;
                }
                return { date: otDate, amount: otAmount };
            })(),
            split_schedule: null,
        },
        sweep: {
            enabled: Boolean(filePolicy.sweep?.enabled),
            mode: filePolicy.sweep?.mode === 'fixed' ? 'fixed' : 'excess',
            amount_per_period: filePolicy.sweep?.amount_per_period ?? null,
            yield_income_view: (filePolicy.sweep?.yield_income_view as CashPolicyYieldView | null) ?? null,
            opening_mm_balance: filePolicy.sweep?.opening_mm_balance ?? null,
            yield_snapshot: null,
        },
        solver: {
            horizon_months: horizon as 12 | 24 | 36,
            round_to: roundTo as 500 | 1000 | 5000,
        },
    };
}

function buildSummary(
    file: CaseFileV1,
    templateCount: number,
    rebaseDropped?: { oneTime: number; notes: number; recipes?: number },
): CaseFileSummary {
    const drivenLinesByView: Record<View, number> = {
        river_oaks: 0,
        highland_park: 0,
        ga: 0,
        consolidated: 0,
    };
    const driverCounts: Record<string, number> = {};
    let drivenTotal = 0;
    let recipeLineCount = 0;
    let overrideCount = 0;
    for (const view of FILE_VIEWS) {
        for (const line of Object.values(file.lines[view] ?? {})) {
            if (line.driver) {
                drivenLinesByView[view] += 1;
                drivenTotal += 1;
                driverCounts[line.driver] = (driverCounts[line.driver] ?? 0) + 1;
                if (isRecipeObject(line.values)) {
                    recipeLineCount += 1;
                    const overrides = (line.values as Record<string, unknown>).overrides;
                    if (isObject(overrides)) {
                        overrideCount += Object.keys(overrides).length;
                    }
                }
            }
        }
    }
    return {
        drivenLinesByView,
        driverCounts,
        recipeLineCount,
        overrideCount,
        oneTimeItemCount: file.one_time_items.length,
        cashPolicyEnabled: Boolean(file.cash_policy?.enabled),
        noteCount: Object.keys(file.notes ?? {}).length,
        undrivenTemplateLineCount: templateCount - drivenTotal,
        rebaseDroppedOneTimeItems: rebaseDropped?.oneTime,
        rebaseDroppedNotes: rebaseDropped?.notes,
        rebaseRecipesRematerialised: rebaseDropped?.recipes,
    };
}

export function parseCaseFile(text: string, options: ParseCaseFileOptions): ParsedCaseFile {
    const issues: FileIssue[] = [];
    const emptyResult: ParsedCaseFile = {
        file: null,
        inputs: null,
        meta: {},
        notes: {},
        rebase: null,
        target: { kind: 'blocked' },
        issues,
        summary: null,
    };

    if (text.length > MAX_FILE_BYTES) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_too_large,
            severity: 'error',
            path: '$',
            message: 'File exceeds the 5 MB limit.',
        });
        return emptyResult;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid JSON';
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_not_json,
            severity: 'error',
            path: '$',
            message,
        });
        return emptyResult;
    }

    if (!isObject(parsed)) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_not_json,
            severity: 'error',
            path: '$',
            message: 'Root value must be a JSON object.',
        });
        return emptyResult;
    }

    walkUnknownKeys(parsed, '$', issues);

    if (parsed.format !== 'bookkeeper.projection_case') {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_wrong_format,
            severity: 'error',
            path: '$.format',
            message: 'format must be "bookkeeper.projection_case".',
        });
    }
    const formatVersion = parsed.format_version;
    if (formatVersion !== 1 && formatVersion !== 2) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_unsupported_version,
            severity: 'error',
            path: '$.format_version',
            message: 'Only format_version 1 and 2 are supported.',
        });
    }
    if (formatVersion === 1) {
        const linesBlock = isObject(parsed.lines) ? parsed.lines : {};
        for (const view of FILE_VIEWS) {
            const viewRaw = linesBlock[view];
            if (!isObject(viewRaw)) {
                continue;
            }
            for (const [lineKey, lineRaw] of Object.entries(viewRaw)) {
                if (!isObject(lineRaw)) {
                    continue;
                }
                if (isRecipeObject(lineRaw.values)) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_unsupported_version,
                        severity: 'error',
                        path: `$.lines.${view}["${lineKey}"].values`,
                        message: 'Recipe values require format_version 2.',
                    });
                }
            }
        }
    }

    const { schema, schemaFingerprint, today } = options;
    const currentLastClosedDate =
        options.lastClosedMonth.length === 7
            ? lastClosedDateFromFileMonth(options.lastClosedMonth)
            : options.lastClosedMonth;
    const currentFileMonth = fileMonthFromLastClosedDate(currentLastClosedDate);

    const caseBlock = isObject(parsed.case) ? parsed.case : {};
    const nameRaw = typeof caseBlock.name === 'string' ? caseBlock.name : '';
    const trimmedName = nameRaw.trim();
    if (!trimmedName) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_name_missing,
            severity: 'error',
            path: '$.case.name',
            message: 'Case name is required.',
        });
    }

    let target: ParsedCaseFile['target'] = { kind: 'create' };
    const existing = resolveExistingCases(options);
    if (trimmedName) {
        const exact = existing.find((c) => c.name === trimmedName);
        const ci = existing.find(
            (c) => c.name.toLowerCase() === trimmedName.toLowerCase() && c.name !== trimmedName,
        );
        if (exact) {
            target = { kind: 'override', name: exact.name };
            const expiresOn =
                typeof caseBlock.expires_on === 'string' || caseBlock.expires_on === null
                    ? caseBlock.expires_on
                    : null;
            if (
                exact.expired &&
                (expiresOn === null || expiresOn < today)
            ) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_target_expired,
                    severity: 'error',
                    path: '$.case.expires_on',
                    message: `Cannot override expired case "${exact.name}" without a future expires_on.`,
                });
            }
        } else if (ci) {
            target = { kind: 'blocked' };
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_name_case_conflict,
                severity: 'error',
                path: '$.case.name',
                message: `Name "${trimmedName}" differs only by capitalisation from existing case "${ci.name}".`,
            });
        }
    }

    const basis = isObject(parsed.basis) ? parsed.basis : {};
    const fileLastClosed =
        typeof basis.last_closed_month === 'string' ? basis.last_closed_month.trim() : '';
    if (!/^\d{4}-\d{2}$/.test(fileLastClosed)) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_basis_months_mismatch,
            severity: 'error',
            path: '$.basis.last_closed_month',
            message: 'basis.last_closed_month is required as YYYY-MM.',
        });
    }

    let rebaseInfo: ParsedCaseFile['rebase'] = null;
    if (fileLastClosed) {
        const diff = monthIndex(currentFileMonth) - monthIndex(fileLastClosed);
        if (diff < 0) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_basis_ahead,
                severity: 'error',
                path: '$.basis.last_closed_month',
                message: `File basis ${fileLastClosed} is ahead of the current model (${currentFileMonth}).`,
            });
        } else if (diff > 3) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_basis_too_old,
                severity: 'error',
                path: '$.basis.last_closed_month',
                message: `File basis ${fileLastClosed} is more than 3 months behind ${currentFileMonth}.`,
            });
        } else if (diff >= 1 && diff <= 3) {
            rebaseInfo = { needed: true, months: diff };
            if (!options.rebase) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_basis_behind,
                    severity: 'warning',
                    path: '$.basis.last_closed_month',
                    message: `File was written for ${fileLastClosed}; align by calendar month to continue.`,
                });
            }
        }
    }

    const fileLastClosedDate = fileLastClosed
        ? lastClosedDateFromFileMonth(fileLastClosed)
        : currentLastClosedDate;
    const expectedMonths = projectionMonthKeys(fileLastClosedDate);
    if (Array.isArray(basis.months)) {
        const months = basis.months.map(String);
        if (months.length !== 36 || months.some((m, i) => m !== expectedMonths[i])) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_basis_months_mismatch,
                severity: 'error',
                path: '$.basis.months',
                message: 'basis.months must be the 36 projection months after basis.last_closed_month.',
            });
        }
    }

    const fileFingerprint =
        typeof basis.schema_fingerprint === 'string' ? basis.schema_fingerprint : '';
    if (fileFingerprint && fileFingerprint !== schemaFingerprint) {
        pushIssue(issues, {
            code: FILE_ISSUE_CODES.file_schema_fingerprint,
            severity: 'warning',
            path: '$.basis.schema_fingerprint',
            message: 'Schema fingerprint differs from the current model.',
        });
    }

    const consolidatedOnly = Array.isArray(parsed.consolidated_only)
        ? parsed.consolidated_only.map(String)
        : [];

    const linesRaw = isObject(parsed.lines) ? parsed.lines : {};
    for (const viewKey of Object.keys(linesRaw)) {
        if (!FILE_VIEWS.includes(viewKey as View)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_unknown_view,
                severity: 'error',
                path: `$.lines.${viewKey}`,
                message: `Unknown view "${viewKey}".`,
            });
        }
    }
    const parsedLines: CaseFileV1['lines'] = {
        river_oaks: {},
        highland_park: {},
        ga: {},
        consolidated: {},
    };

    const driverPass: Array<{
        view: View;
        lineKey: string;
        line: CaseFileLine;
        path: string;
    }> = [];

    for (const view of FILE_VIEWS) {
        const viewRaw = linesRaw[view];
        if (viewRaw === undefined) {
            continue;
        }
        if (!isObject(viewRaw)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_unknown_view,
                severity: 'error',
                path: `$.lines.${view}`,
                message: `View "${view}" must be an object.`,
            });
            continue;
        }
        for (const [lineKey, lineRaw] of Object.entries(viewRaw)) {
            const path = `$.lines.${view}["${lineKey}"]`;
            if (!isObject(lineRaw)) {
                continue;
            }
            walkUnknownKeys(lineRaw, path, issues, true);
            const entry = schema.find((row) => row.key === lineKey);
            if (!entry) {
                if (RETIRED_LINE_KEYS.has(lineKey)) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_retired_line,
                        severity: 'warning',
                        path,
                        message: `${lineKey} was retired when the chart of accounts changed (2026-09); ignored.`,
                    });
                    continue;
                }
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_unknown_line,
                    severity: 'error',
                    path,
                    message: `Unknown line key ${lineKey}.`,
                });
                continue;
            }
            if (consolidatedOnly.includes(lineKey) && view !== 'consolidated') {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_consolidated_only_conflict,
                    severity: 'error',
                    path,
                    message: `${lineDisplayName(schema, lineKey)} is consolidated-only and must not appear under ${view}.`,
                });
                continue;
            }
            if (typeof lineRaw.label === 'string') {
                if (trimSchemaLabel(lineRaw.label) !== trimSchemaLabel(entry.label)) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_label_mismatch,
                        severity: 'error',
                        path: `${path}.label`,
                        message: `Label on ${lineDisplayName(schema, lineKey)} does not match the schema.`,
                    });
                }
            }
            const driver =
                lineRaw.driver === null || typeof lineRaw.driver === 'string'
                    ? lineRaw.driver
                    : null;
            const rawValues = lineRaw.values;
            let normalizedValues: CaseFileLine['values'] = Array(36).fill(null);
            if (
                Array.isArray(rawValues) ||
                typeof rawValues === 'number' ||
                (isObject(rawValues) && rawValues !== null)
            ) {
                normalizedValues = rawValues as CaseFileLine['values'];
            }
            const parsedLine: CaseFileLine = {
                label: trimSchemaLabel(entry.label),
                unit: entry.unit,
                allowed_drivers: allowedDrivers(schema, lineKey, view, consolidatedOnly),
                driver,
                values: normalizedValues,
                params: isObject(lineRaw.params) ? lineRaw.params : {},
            };
            if (Array.isArray(lineRaw.ignore_group_members)) {
                parsedLine.ignore_group_members = lineRaw.ignore_group_members.map(String);
            }
            parsedLines[view][lineKey] = parsedLine;
            if (driver) {
                driverPass.push({ view, lineKey, line: parsedLine, path });
            }
        }
    }

    const lineContextInputs = buildInputsStubFromParsedLines(parsedLines, consolidatedOnly);
    for (const view of FILE_VIEWS) {
        for (const [lineKey, line] of Object.entries(parsedLines[view])) {
            if (!line.driver) {
                continue;
            }
            const path = `$.lines.${view}["${lineKey}"]`;
            if (!hasNonCarryForwardDriver(schema, lineKey, view, consolidatedOnly, lineContextInputs)) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_line_not_drivable,
                    severity: 'error',
                    path,
                    message: `${lineDisplayName(schema, lineKey)} is not drivable in view ${view}.`,
                });
                delete parsedLines[view][lineKey];
                const idx = driverPass.findIndex(
                    (item) => item.view === view && item.lineKey === lineKey,
                );
                if (idx >= 0) {
                    driverPass.splice(idx, 1);
                }
            }
        }
    }

    for (const key of consolidatedOnly) {
        for (const view of LOCATION_VIEWS) {
            if (parsedLines[view][key]) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_consolidated_only_conflict,
                    severity: 'error',
                    path: `$.lines.${view}["${key}"]`,
                    message: `${lineDisplayName(schema, key)} is consolidated-only and must not appear under ${view}.`,
                });
            }
        }
    }
    for (const [lineKey] of Object.entries(parsedLines.consolidated)) {
        const entry = schema.find((row) => row.key === lineKey);
        if (entry?.tab === 'IS' && !consolidatedOnly.includes(lineKey)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_consolidated_only_conflict,
                severity: 'error',
                path: `$.lines.consolidated["${lineKey}"]`,
                message: `IS line ${lineDisplayName(schema, lineKey)} under consolidated must be listed in consolidated_only.`,
            });
        }
    }

    const builtInputs = emptyInputs();
    builtInputs.consolidated_only = [...consolidatedOnly];
    const materializedLines: Record<
        string,
        Record<string, { values: (number | null)[]; params: Record<string, unknown>; driver: string | null; recipe?: Recipe | null }>
    > = {};

    let rebaseDropped = { oneTime: 0, notes: 0, recipes: 0 };
    const recipeDefaults = options.recipeDefaults ?? {};
    const driftPct = recipeDefaults.anchor_drift_pct ?? 0.15;
    const rationaleMaxWords = recipeDefaults.rationale_max_words ?? 60;

    for (const item of driverPass) {
        const { view, lineKey, line, path } = item;
        const driver = line.driver as DriverType;
        let recipe: Recipe | null = null;
        let values: (number | null)[] | null = null;

        if (isRecipeObject(line.values)) {
            const validated = validateRecipe(line.values, {
                lastClosedMonth: fileLastClosedDate,
                driverType: driver,
                projectionMonths: expectedMonths,
                pathPrefix: `${path}.values`,
                rationaleMaxWords,
            });
            issues.push(...validated.issues);
            if (validated.recipe) {
                recipe = validated.recipe;
                if (options.history && recipe.anchor.basis && recipe.anchor.basis !== 'manual') {
                    const historyValue = anchorFromHistory(
                        recipe.anchor.basis,
                        recipe.anchor.basis === 'trailing_avg_per_period'
                            ? (recipe.anchor.runs ?? 3)
                            : (recipe.anchor.months ?? 3),
                        {
                            view,
                            lineKey,
                            driverType: driver,
                            params: line.params as import('../engine/types').DriverParams,
                            history: options.history,
                            schema,
                            lastClosedMonth: fileLastClosedDate,
                        },
                    );
                    if (historyValue != null) {
                        recipe = {
                            ...recipe,
                            anchor: { ...recipe.anchor, history_value: historyValue },
                        };
                        const driftIssue = checkAnchorDrift(
                            recipe.anchor.value,
                            historyValue,
                            driftPct,
                            `${path}.values.anchor.value`,
                            {
                                view,
                                lineKey,
                                driverType: driver,
                                anchor: recipe.anchor,
                                lastClosedMonth: fileLastClosedDate,
                                schema,
                            },
                        );
                        if (driftIssue) {
                            issues.push(driftIssue);
                        }
                    }
                }
                const seasonalityResult = resolveSeasonalityFactors(recipe, {
                    view,
                    lineKey,
                    driverType: driver,
                    params: line.params as import('../engine/types').DriverParams,
                    history: options.history,
                    schema,
                    lastClosedMonth: fileLastClosedDate,
                    pathPrefix: `${path}.values`,
                });
                if (seasonalityResult.error) {
                    issues.push(seasonalityResult.error);
                }
                const seasonalityFactors = seasonalityResult.factors;
                if (!issues.some((i) => i.severity === 'error' && i.path.startsWith(`${path}.values`))) {
                    const materialised = materialise(recipe, {
                        lastClosedMonth: fileLastClosedDate,
                        driverType: driver,
                        unit: driverInputUnit(driver as Parameters<typeof driverInputUnit>[0]),
                        seasonalityFactors,
                        projectionMonths: expectedMonths,
                    });
                    values = materialised;
                }
            }
        } else {
            values = materializeValues(line.values, expectedMonths, `${path}.values`, issues);
        }

        if (!values) {
            continue;
        }
        materializedLines[view] ??= {};
        materializedLines[view]![lineKey] = {
            values,
            params: { ...line.params },
            driver: line.driver,
            recipe,
        };
    }

    if (rebaseInfo?.needed && options.rebase && rebaseInfo.months >= 1) {
        const rebaseResult = rebaseMaterialized(
            {
                lines: materializedLines,
                oneTimeItems: Array.isArray(parsed.one_time_items)
                    ? (parsed.one_time_items as CaseFileOneTimeItem[])
                    : [],
                notes: isObject(parsed.notes) ? (parsed.notes as Record<string, string>) : {},
                projectionMonths: expectedMonths,
                lastClosedMonth: currentLastClosedDate,
                history: options.history,
                schema,
            },
            rebaseInfo.months,
        );
        Object.assign(materializedLines, rebaseResult.lines);
        parsed.one_time_items = rebaseResult.oneTimeItems;
        parsed.notes = rebaseResult.notes;
        rebaseDropped = {
            oneTime: rebaseResult.droppedOneTimeItems.length,
            notes: rebaseResult.droppedNotes.length,
            recipes: rebaseResult.rematerialisedRecipes,
        };
        rebaseInfo = { needed: true, months: rebaseInfo.months };
        issues.splice(
            issues.findIndex((i) => i.code === FILE_ISSUE_CODES.file_basis_behind),
            1,
        );
    }

    for (const item of driverPass) {
        const { view, lineKey, line, path } = item;
        const mat = materializedLines[view]?.[lineKey];
        if (!mat || !line.driver) {
            continue;
        }
        const driver = line.driver as DriverType;
        let values = mat.values;
        let params = mat.params;

        if (driver === 'pct_of_line' || driver === 'dollar_per_line_unit') {
            const ref = params.ref;
            if (typeof ref !== 'string' || !ref) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_params_invalid,
                    severity: 'error',
                    path: `${path}.params.ref`,
                    message: `${lineDisplayName(schema, lineKey)} ${driver} requires params.ref.`,
                });
            }
        } else if (driver === 'labor_buildup') {
            if (lineKey !== 'is.payroll_wages' || view === 'consolidated') {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_params_invalid,
                    severity: 'error',
                    path,
                    message: `${lineDisplayName(schema, lineKey)} labor_buildup is only allowed on Payroll – Wages at location views.`,
                });
            }
            const validCadences = new Set(['biweekly', 'semimonthly', 'weekly', 'monthly']);
            const extraKeys = Object.keys(params).filter(
                (key) => !['cadence', 'anchor_date', 'net_of_benefits_pct'].includes(key),
            );
            const cadence = params.cadence;
            const anchorDate = params.anchor_date;
            const netPct = params.net_of_benefits_pct ?? 0;
            if (
                extraKeys.length > 0 ||
                (cadence != null &&
                    (typeof cadence !== 'string' || !validCadences.has(cadence))) ||
                (anchorDate != null &&
                    (typeof anchorDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(anchorDate))) ||
                !isFiniteNumber(netPct)
            ) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_params_invalid,
                    severity: 'error',
                    path: `${path}.params`,
                    message: `${lineDisplayName(schema, lineKey)} labor_buildup has invalid params.`,
                });
            } else {
                params = {
                    net_of_benefits_pct: netPct,
                    ...(typeof cadence === 'string' ? { cadence } : {}),
                    ...(typeof anchorDate === 'string' ? { anchor_date: anchorDate } : {}),
                };
            }
        } else if (driver === 'dollar_per_pay_period') {
            const cadence = params.cadence;
            const anchorDate = params.anchor_date;
            const validCadences = new Set(['biweekly', 'semimonthly', 'weekly', 'monthly']);
            const extraKeys = Object.keys(params).filter(
                (key) => key !== 'cadence' && key !== 'anchor_date',
            );
            const invalid =
                extraKeys.length > 0 ||
                typeof cadence !== 'string' ||
                !validCadences.has(cadence) ||
                typeof anchorDate !== 'string' ||
                !/^\d{4}-\d{2}-\d{2}$/.test(anchorDate);
            if (invalid) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_params_invalid,
                    severity: 'error',
                    path: `${path}.params`,
                    message: `${lineDisplayName(schema, lineKey)} dollar_per_pay_period requires params.cadence and params.anchor_date.`,
                });
            } else {
                params = { cadence, anchor_date: anchorDate };
            }
        } else if (driver === 's_curve') {
            for (const key of ['start', 'end', 'midpoint', 'steepness'] as const) {
                if (!isFiniteNumber(params[key])) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_params_invalid,
                        severity: 'error',
                        path: `${path}.params.${key}`,
                        message: `${lineDisplayName(schema, lineKey)} s_curve requires params.${key}.`,
                    });
                }
            }
            if (
                isFiniteNumber(params.start) &&
                isFiniteNumber(params.end) &&
                isFiniteNumber(params.midpoint) &&
                isFiniteNumber(params.steepness)
            ) {
                const materialized = materialize({
                    start: params.start as number,
                    end: params.end as number,
                    midpoint: params.midpoint as number,
                    steepness: params.steepness as number,
                });
                const differs = materialized.some(
                    (v, i) => Math.abs(v - (values[i] ?? 0)) > 0.0005,
                );
                if (differs) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_values_ignored,
                        severity: 'warning',
                        path: `${path}.values`,
                        message: `S-curve values on ${lineDisplayName(schema, lineKey)} were replaced with materialised values.`,
                    });
                }
                values = materialized;
            }
        } else if (driver !== 'carry_forward' && driver !== 'ignore') {
            if (Object.keys(params).length > 0) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_params_invalid,
                    severity: 'error',
                    path: `${path}.params`,
                    message: `${lineDisplayName(schema, lineKey)} does not accept params for driver ${driver}.`,
                });
            }
            params = {};
        }

        if (driver === 'carry_forward' || driver === 'ignore' || driver === 'labor_buildup') {
            const hasNonNull = values.some((v) => v !== null);
            if (hasNonNull) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_values_ignored,
                    severity: 'warning',
                    path: `${path}.values`,
                    message: `Values on ${lineDisplayName(schema, lineKey)} are ignored for ${driver}.`,
                });
            }
            values = Array(36).fill(null);
            if (driver !== 'labor_buildup') {
                params = {};
            }
        }

        if (PERCENT_DRIVERS.has(driver)) {
            for (let i = 0; i < values.length; i += 1) {
                const v = values[i];
                if (v != null && Math.abs(v) > 1) {
                    pushIssue(issues, {
                        code: FILE_ISSUE_CODES.file_percent_scale,
                        severity: 'warning',
                        path: `${path}.values[${i}]`,
                        message: `${lineDisplayName(schema, lineKey)} looks like whole percents; 5% is 0.05.`,
                    });
                    break;
                }
            }
        }

        if (!builtInputs.drivers[view]) {
            builtInputs.drivers[view] = {};
        }
        const state: LineDriverState = { active: driver, by_type: {} };
        if (driver === 'ignore') {
            state.by_type = {};
        } else if (driver === 'carry_forward') {
            state.by_type.carry_forward = { values: Array(36).fill(null) };
        } else if (driver === 'labor_buildup') {
            state.by_type.labor_buildup = { values: Array(36).fill(null), params };
        } else {
            const recipe = mat.recipe ?? null;
            state.by_type[driver] = recipe ? { values, params, recipe } : { values, params };
        }
        builtInputs.drivers[view]![lineKey] = state;

        if (driver === 'ignore') {
            const memberGroup = ignoreGroupForLine(schema, lineKey);
            if (memberGroup) {
                for (const member of memberGroup.members) {
                    builtInputs.drivers[view]![member] = { active: 'ignore', by_type: {} };
                }
            }
        }
    }

    for (const group of listEligibleIgnoreGroups(schema)) {
        for (const view of FILE_VIEWS) {
            let ignored = 0;
            let present = 0;
            for (const member of group.members) {
                const driver = parsedLines[view][member]?.driver;
                if (driver === undefined && !parsedLines[view][member]) {
                    continue;
                }
                present += 1;
                if (driver === 'ignore') {
                    ignored += 1;
                }
            }
            if (present > 0 && ignored > 0 && ignored < group.members.length) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_ignore_group_partial,
                    severity: 'error',
                    path: `$.lines.${view}`,
                    message: `Ignore group ${lineDisplayName(schema, group.parent)} is only partially ignored in ${view}.`,
                });
            }
        }
    }

    for (const { view, lineKey, line, path } of driverPass) {
        if (!line.driver) {
            continue;
        }
        const allowed = allowedDrivers(
            schema,
            lineKey,
            view,
            consolidatedOnly,
            builtInputs,
        );
        if (!allowed.includes(line.driver as DriverType)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_driver_not_allowed,
                severity: 'error',
                path: `${path}.driver`,
                message: `Driver "${line.driver}" is not allowed on ${lineDisplayName(schema, lineKey)} in ${view}.`,
            });
        }
        if (line.driver === 'pct_of_line' || line.driver === 'dollar_per_line_unit') {
            const ref = line.params.ref;
            if (typeof ref === 'string' && wouldCreateCycle(schema, builtInputs, view, lineKey, ref)) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_pct_of_line_cycle,
                    severity: 'error',
                    path: `${path}.params.ref`,
                    message: `${line.driver} reference on ${lineDisplayName(schema, lineKey)} would create a cycle.`,
                });
            }
        }
    }

    const oneTimeRaw = Array.isArray(parsed.one_time_items) ? parsed.one_time_items : [];
    const oneTimeItems: OneTimeItem[] = [];
    for (let i = 0; i < oneTimeRaw.length; i += 1) {
        const raw = oneTimeRaw[i];
        const path = `$.one_time_items[${i}]`;
        if (!isObject(raw)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path,
                message: 'One-time item must be an object.',
            });
            continue;
        }
        const month = typeof raw.month === 'string' ? raw.month : '';
        const view = raw.view as View;
        const line_key = typeof raw.line_key === 'string' ? raw.line_key : '';
        const amount = raw.amount;
        const mode = raw.mode;
        if (!expectedMonths.includes(month)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path: `${path}.month`,
                message: 'One-time item month must be a projection month.',
            });
        }
        const entry = schema.find((row) => row.key === line_key);
        if (!entry || entry.kind !== 'input') {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path: `${path}.line_key`,
                message: 'One-time item line_key must be an input line.',
            });
        }
        const validViews = allowedDrivers(schema, line_key, view, consolidatedOnly);
        if (!validViews.length && entry) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path: `${path}.view`,
                message: `View ${view} is not valid for ${lineDisplayName(schema, line_key)}.`,
            });
        }
        if (!isFiniteNumber(amount)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path: `${path}.amount`,
                message: 'One-time item amount must be a finite number.',
            });
        }
        if (mode !== 'incremental' && mode !== 'already_in_driver') {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_one_time_invalid,
                severity: 'error',
                path: `${path}.mode`,
                message: 'One-time item mode must be incremental or already_in_driver.',
            });
        }
        oneTimeItems.push({
            id: typeof raw.id === 'string' ? raw.id : crypto.randomUUID(),
            month,
            view,
            line_key,
            amount: amount as number,
            mode: mode as OneTimeItem['mode'],
            notes: typeof raw.notes === 'string' ? raw.notes : undefined,
        });
    }
    builtInputs.one_time_items = oneTimeItems;

    builtInputs.cash_policy = parseCashPolicy(parsed.cash_policy, '$.cash_policy', issues);

    const migratedInputs = migrateTotalOrdersDriversToAvgOrders(builtInputs);

    const notesOut: Record<string, string> = {};
    const notesRaw = isObject(parsed.notes) ? parsed.notes : {};
    for (const [key, text] of Object.entries(notesRaw)) {
        if (typeof text !== 'string') {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_note_key_invalid,
                severity: 'error',
                path: `$.notes["${key}"]`,
                message: 'Note values must be strings.',
            });
            continue;
        }
        if (!NOTE_CELL_RE.test(key)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_note_key_invalid,
                severity: 'error',
                path: `$.notes["${key}"]`,
                message: `Note key "${key}" is not a valid projected cell key.`,
            });
            continue;
        }
        const lineKey = key.split(':')[1] ?? '';
        if (!schema.find((row) => row.key === lineKey)) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_note_key_invalid,
                severity: 'error',
                path: `$.notes["${key}"]`,
                message: `Note key references unknown line ${lineKey}.`,
            });
            continue;
        }
        const period = key.split(':').at(-1) ?? '';
        const t = Number(period);
        if (Number.isFinite(t) && t <= 0) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_note_key_invalid,
                severity: 'error',
                path: `$.notes["${key}"]`,
                message: 'Shared historical note keys are not supported in case files.',
            });
            continue;
        }
        notesOut[key] = text;
    }

    for (const { view, lineKey, line, path } of driverPass) {
        const rawValues = line.values;
        if (isRecipeObject(rawValues)) {
            const recipeRaw = rawValues as Record<string, unknown>;
            const hasRationale =
                typeof recipeRaw.rationale === 'string' && recipeRaw.rationale.trim().length > 0;
            const hasShape =
                recipeRaw.path != null ||
                recipeRaw.growth != null ||
                recipeRaw.seasonality != null;
            const labelNoteKey = `${view}:${lineKey}:label`;
            const methodologyNoteKey = `${view}:${lineKey}:methodology`;
            const hasLabelNote =
                typeof notesRaw[labelNoteKey] === 'string' && notesRaw[labelNoteKey].trim().length > 0;
            const hasMethodologyNote =
                typeof notesRaw[methodologyNoteKey] === 'string' &&
                notesRaw[methodologyNoteKey].trim().length > 0;
            if (hasShape && !hasRationale && !hasLabelNote && !hasMethodologyNote) {
                pushIssue(issues, {
                    code: FILE_ISSUE_CODES.file_rationale_missing,
                    severity: 'warning',
                    path: `${path}.values`,
                    message: `Recipe on ${view} ${lineKey} has no rationale, methodology note, or label note.`,
                });
            }
            continue;
        }
        if (!isNonFlatRawValues(rawValues)) {
            continue;
        }
        const labelNoteKey = `${view}:${lineKey}:label`;
        const methodologyNoteKey = `${view}:${lineKey}:methodology`;
        const hasLabelNote =
            typeof notesRaw[labelNoteKey] === 'string' && notesRaw[labelNoteKey].trim().length > 0;
        const hasMethodologyNote =
            typeof notesRaw[methodologyNoteKey] === 'string' &&
            notesRaw[methodologyNoteKey].trim().length > 0;
        if (!hasLabelNote && !hasMethodologyNote) {
            pushIssue(issues, {
                code: FILE_ISSUE_CODES.file_rationale_missing,
                severity: 'warning',
                path: `${path}.values`,
                message: `Non-flat values on ${view} ${lineKey} have no rationale, methodology note, or label note.`,
            });
        }
    }

    const hasError = issues.some((i) => i.severity === 'error');
    const file: CaseFileV1 = {
        format: 'bookkeeper.projection_case',
        format_version: formatVersion === 2 ? 2 : 1,
        case: {
            name: trimmedName,
            description: typeof caseBlock.description === 'string' ? caseBlock.description : '',
            expires_on:
                typeof caseBlock.expires_on === 'string' || caseBlock.expires_on === null
                    ? caseBlock.expires_on
                    : null,
        },
        basis: {
            last_closed_month: fileLastClosed || currentFileMonth,
            months: expectedMonths,
            schema_fingerprint: fileFingerprint || schemaFingerprint,
        },
        consolidated_only: consolidatedOnly,
        lines: parsedLines,
        one_time_items: oneTimeRaw as CaseFileOneTimeItem[],
        cash_policy: isObject(parsed.cash_policy)
            ? (parsed.cash_policy as unknown as CaseFileCashPolicy)
            : blankCashPolicy(),
        notes: notesRaw as Record<string, string>,
    };

    const templateCount = collectTemplateLineKeys(schema, listEligibleIgnoreGroups(schema)).length;
    const summary = buildSummary(file, templateCount, rebaseDropped);

    return {
        file,
        inputs: hasError ? null : migratedInputs,
        meta: {
            description: file.case.description,
            expires_on: file.case.expires_on,
        },
        notes: notesOut,
        rebase: rebaseInfo,
        target,
        issues,
        summary,
    };
}
